const key = 'fp-session-id';
const isBrowser = typeof window !== 'undefined';
const isTest = process.env.NODE_ENV === 'test';
const queryParamKey = 'xfpsessionid';

// React Native detection - check if we're in a React Native environment
const isReactNative = () => {
  try {
    // In React Native, global.navigator exists but doesn't have the same properties as web navigator
    return typeof global !== 'undefined' && global.navigator && global.navigator.product === 'ReactNative';
  } catch {
    return false;
  }
};

// Safe window access that handles React Native environment
const safeWindowAccess = () => {
  if (!isBrowser) {
    return null;
  }

  try {
    // In React Native, window might exist but location/top might not
    if (isReactNative()) {
      return null;
    }

    return window;
  } catch {
    return null;
  }
};

const uuidv4 = () => {
  const timestamp = Date.now();
  const randomPart = Math.floor(Math.random() * 1000000000);

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (((timestamp + randomPart) * Math.random() * 16) % 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const getParamValue = (param: string, url: string): string | null => {
  const urlObject = new URL(url);
  return urlObject.searchParams.get(param);
};

const getParamFromHost = (param: string) => {
  try {
    const safeWindow = safeWindowAccess();
    if (!safeWindow?.top?.location) {
      return null;
    }

    const currentUrl = safeWindow.top.location.href;
    return currentUrl ? getParamValue(param, currentUrl) : null;
  } catch {
    return null;
  }
};

const getSessionIdFromQueryParam = (): string | null => {
  if (!isBrowser) {
    return null;
  }

  const safeWindow = safeWindowAccess();
  if (!safeWindow?.location) {
    return getParamFromHost(queryParamKey);
  }

  return getParamValue(queryParamKey, safeWindow.location.href) || getParamFromHost(queryParamKey);
};

const isSessionStorageAvailable = (): boolean => {
  const safeWindow = safeWindowAccess();
  if (!isTest && (!isBrowser || !safeWindow?.sessionStorage)) {
    return false;
  }

  try {
    const testKey = 'test';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch (_e) {
    return false;
  }
};

const create = (): string => {
  const sessionId = getSessionIdFromQueryParam() || uuidv4();
  if (isSessionStorageAvailable()) {
    try {
      sessionStorage.setItem(key, sessionId);
    } catch (e) {
      console.error('Failed to create session id', e);
    }
  }
  return sessionId;
};

const get = () => {
  const querySessionId = getSessionIdFromQueryParam();

  if (querySessionId) {
    return querySessionId;
  }

  return isSessionStorageAvailable() ? sessionStorage.getItem(key) : null;
};

const getSessionId = () => get() || create();

const addSessionIdToQueryParam = (url: string, sessionId: string = getSessionId()) => {
  const urlObject = new URL(url);
  urlObject.searchParams.set(queryParamKey, sessionId);
  return urlObject.toString();
};

const getSessionIdFromStorage = (): string | null => {
  const safeWindow = safeWindowAccess();
  if (!isBrowser || !safeWindow?.sessionStorage) {
    return null;
  }
  try {
    return sessionStorage.getItem(key) || null;
  } catch {
    return null;
  }
};

export { addSessionIdToQueryParam, getSessionIdFromQueryParam, getSessionIdFromStorage, uuidv4 };
export default getSessionId;
